/**
 * CTA Tracking Module
 * 
 * Wrapper for BookingsCTA analytics tracking with consent validation
 * Task: 112-141-T003
 */

import { useAnalytics } from '../../composables/useAnalytics'
import { hasAnalyticsConsent } from '../../composables/useAnalyticsConsent'

/**
 * Track CTA button click event
 * 
 * Emits 'cta_clicked' analytics event with full payload.
 * Respects user consent (GDPR/CCPA) - skips tracking if user opted out.
 * Handles tracking errors gracefully (errors logged, UI not affected).
 * 
 * @param {string} location - CTA location identifier (hero, mid, footer)
 * @param {string} product - Product context (review, verify, insight)
 * @param {string} buttonText - Button text displayed to user
 * 
 * @example
 * ```typescript
 * // In BookingsCTA component
 * trackCtaClick('hero', 'review', 'Book a Demo')
 * ```
 */
export function trackCtaClick(
  location: string,
  product: string,
  buttonText: string
): void {
  // Check user consent before emitting event
  if (!hasAnalyticsConsent()) {
    console.debug('[CTA Tracking] Consent denied, event not emitted', {
      location,
      product,
      buttonText,
    })
    return
  }

  try {
    // Get analytics composable and emit event
    const analytics = useAnalytics()
    analytics.emitCTAClicked(location, product, buttonText)

    // Dev mode logging for verification
    if (process.env.NODE_ENV === 'development') {
      console.debug('[CTA Tracking] Event emitted:', {
        event_name: 'cta_clicked',
        location,
        product,
        button_text: buttonText,
        destination: 'bookings',
      })
    }
  } catch (error) {
    // Graceful degradation: Log error but don't throw
    // Analytics failures should never crash the UI (per AT-004)
    console.error('[CTA Tracking] Error emitting event:', error)
  }
}
